import { Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import { TC } from 'abis/types'
import { ButtonPurple } from 'components/Button'
import Column, { AutoColumn } from 'components/Column'
import Modal from 'components/Modal'
import Row from 'components/Row'
import { MAX_WIDTH_MEDIA_BREAKPOINT } from 'components/Tokens/constants'
import { MouseoverTooltip } from 'components/Tooltip'
import { IUserInfo } from 'graphql/competition/UserInfo'
import { useCompetitionContract } from 'hooks/useContract'
import { useWindowSize } from 'hooks/useWindowSize'
import React, { useMemo, useState } from 'react'
import { useTwitterIsBound } from 'state/competition/hooks'
import { Status } from 'state/interfaces'
import { useTransactionAdderWithCallback } from 'state/transactions/hooks'
import styled from 'styled-components/macro'
import { Autoplay, Controller, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { CloseIcon, ThemedText } from 'theme'
import { nowInSeconds } from 'utils/date'

import { agent } from '../api/agent'
import { SignTaskReq } from '../api/interfaces/sign-task.interface'
import { TaskStatus } from '../api/interfaces/task.interface'
import { useStatus } from '../hooks/useStatus'
import BindTwitter from './BindTwitter'
import useTasksByWallet from './hooks/useTasksByWallet'
import { ITaskWithContent } from './interfaces/task-with-content.interface'

const Wrapper = styled.div`
  position: relative;
  max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT};
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 2rem;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
`

const TotalInfoWrapper = styled(Swiper)<{ pt: string }>`
  width: 100%;
  display: flex;
  //gap: 1rem;
  padding-bottom: ${({ pt }) => pt} !important;
  /* flex-direction: column; */

  /* @media only screen and (max-width: 767px) {
    flex-direction: row;
    gap: 1rem;
  }

  @media only screen and (max-width: 440px) {
    flex-direction: column;
    gap: 1rem;
  } */
`

const InfoContainer = styled.div<{
  isFirst?: boolean
  hideOnDesctop?: boolean
  hideOnMobile?: boolean
  width?: string
  isActive: boolean
}>`
  position: relative;
  display: ${({ hideOnDesctop }) => (hideOnDesctop ? 'none' : 'flex')};
  flex-direction: column;
  background: ${({ theme }) => theme.backgroundSwap};
  box-shadow: ${({ isActive, theme }) => (isActive ? ` 0px 0px 9px -1px rgba(11,120,62,1)` : '')};
  border: 1px solid ${({ theme }) => theme.backgroundSwapOutline};
  gap: 0.5rem;
  border-radius: 12px;
  padding: 12px 60px 12px 12px;
  width: ${({ width }) => width || '100%'};
  height: 137px;
  min-width: ${({ width }) => (width ? '' : '200px')};
  @media only screen and (max-width: 767px) {
    ${({ isFirst }) =>
      isFirst &&
      `
  grid-column-start:1;
  grid-column-end:3;
`}
  }
  @media only screen and (max-width: 440px) {
    grid-column-start: 1;
    grid-column-end: 3;
    display: ${({ hideOnMobile }) => (hideOnMobile ? 'none' : 'flex')};
  }
`

const ComingSoonContainer = styled.div`
  position: absolute;
  z-index: 20;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #181337;
  opacity: 0.9;
  border-radius: 12px;
  padding: 12px;
`

const Label = styled.div`
  font-size: 16px;
  line-height: 24px;
  text-overflow: ellipsis;
  -ms-text-overflow: ellipsis;
  -o-text-overflow: ellipsis;
  text-overflow: ellipsis;
  overflow: hidden;
  -ms-line-clamp: 1;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  display: -webkit-box;
  display: box;
  word-wrap: break-word;
  -webkit-box-orient: vertical;
  box-orient: vertical;
  color: white;
`

const Desc = styled.div<{ bigContent?: boolean }>`
  font-size: 14px;
  color: ${({ theme }) => theme.textSecondary};
  text-overflow: ellipsis;
  -ms-text-overflow: ellipsis;
  -o-text-overflow: ellipsis;
  text-overflow: ellipsis;
  overflow: hidden;
  -ms-line-clamp: 3;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  display: -webkit-box;
  display: box;
  word-wrap: break-word;
  -webkit-box-orient: vertical;
  box-orient: vertical;
`

const PercentWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 30%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  font-size: 26px;
  font-weight: 600;
  background: linear-gradient(90deg, #1e28a9 0.78%, #8713d6 100%);
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-items: start;
  justify-content: start;
`

const HiddenScroll = styled.div`
  border-radius: 20px;
  width: 100%;
  overflow-y: hidden;
  max-height: 500px;
  position: relative;
`

const CloseIconBg = styled(CloseIcon)`
  right: 10px;
  padding: 3px;
  top: 10px;
  position: absolute;
  background: ${({ theme }) => theme.backgroundOutline};
  border-radius: 50%;
`

const ModalWrapper = styled.div`
  background-color: ${({ theme }) => theme.backgroundSurface};
  border-radius: 20px;
  outline: 1px solid ${({ theme }) => theme.backgroundOutline};
  width: 100%;
  padding: 16px;
  overflow-y: scroll;
  max-height: 500px;

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-thumb {
    width: 3px;
    background: gray;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-track {
  }
`

interface ITaskBlock {
  task: ITaskWithContent
  action: () => void
  width?: string
  isFirst?: boolean
  bigContent?: boolean
  id?: string
  userInfo: IUserInfo
  competitionContract: TC | null
}

const TaskBlock = ({ task, isFirst, width, bigContent, id, action, userInfo, competitionContract }: ITaskBlock) => {
  const { account, provider } = useWeb3React()
  const buttonStatus = useStatus()
  const isComingSoon = useMemo(() => {
    return !task.content
  }, [task])

  const days = useMemo(() => {
    return task.duration / 86400
  }, [task])

  const signTask = async () => {
    if (!provider || !account || !competitionContract) return
    let signature
    const timestamp = nowInSeconds()
    const signTaskReq: SignTaskReq = { id: task.id, timestamp, url: task.docLink }

    buttonStatus.setPending()
    try {
      signature = await provider.getSigner().signMessage(JSON.stringify(signTaskReq))
    } catch (e) {
      console.warn(e)
      buttonStatus.setError()
      return
    }
    const res = await agent.competition.signTask(account, signTaskReq, signature)
    window.open(task.docLink, '_blank')
    console.log(7777, res)

    // let hash
    // try {
    //   hash = await competitionContract.updateUserMultiplier(res.multiplier, res.id, res.start, res.end, res.signature)
    //   await confirmTask({ id: +res.id, wallet: account })
    // } catch (e) {
    //   console.warn(e)
    //   buttonStatus.setError()
    //   return
    // }

    buttonStatus.setSuccess()
    // addTransactionWithCb(hash, {
    //   type: TransactionType.TASK,
    //   taskName: '1',
    // })
  }

  const btn = useMemo(() => {
    if (task.status === TaskStatus.ACTIVE) {
      return {
        fn: () => null,
        text: 'Active task',
      }
    }
    if (task.status === TaskStatus.CONFIRMED) {
      const multiplierEnded = nowInSeconds() > Number(userInfo?.data?.user?.multiplierEnd)

      return {
        fn: multiplierEnded ? signTask : () => null,
        text: 'Activate',
        disabled: !multiplierEnded,
        toaster: multiplierEnded ? '' : 'You already have an active multiplier, wait until it expires.',
      }
    }
    if (task.status === TaskStatus.USED) {
      return {
        fn: () => null,
        text: 'Used',
        opacity: '0.5',
      }
    }

    return {
      fn: signTask,
      text: 'Activate',
    }
  }, [task, userInfo])

  return (
    <InfoContainer isFirst={isFirst} width={width} id={id || ''} isActive={task.status === TaskStatus.ACTIVE}>
      {(isComingSoon || task.status === TaskStatus.CONFIRMED) && (
        <ComingSoonContainer>
          <Label>{isComingSoon ? 'Coming soon' : 'Completed'}</Label>
        </ComingSoonContainer>
      )}

      <Row gap="0,5rem" height="100%">
        <Column gap="sm" justify="space-between" width="80%" height="100%">
          <Column>
            <Label>{task.title}</Label>
            <Desc bigContent={bigContent}>{task.description}</Desc>
          </Column>
          <ButtonWrapper>
            <ButtonPurple
              disabled={buttonStatus.isPending}
              onClick={btn.fn}
              style={{
                opacity: btn.opacity || '1',
                padding: '4px 10px',
                width: 'auto',
                fontWeight: 'normal',
                fontSize: '16px',
                borderRadius: '12px',
                background: task.status === TaskStatus.ACTIVE ? '#0B783E' : '',
                color: task.status === TaskStatus.ACTIVE ? 'white' : '',
              }}
              width="fit-content"
            >
              {btn.toaster ? (
                <MouseoverTooltip text={btn.toaster} placement="right">
                  <Trans>{btn.text}</Trans>
                </MouseoverTooltip>
              ) : (
                <Trans>{btn.text}</Trans>
              )}
            </ButtonPurple>
          </ButtonWrapper>
        </Column>
        <PercentWrapper>
          <div>{task.multiplier}%</div>
          <Desc>
            For {days} day{days > 1 ? 's' : ''}
          </Desc>
        </PercentWrapper>
      </Row>
    </InfoContainer>
  )
}

interface ITasksProps {
  userInfo: IUserInfo
}

const Tasks = ({ userInfo }: ITasksProps) => {
  const { account, provider } = useWeb3React()
  const competitionContract = useCompetitionContract()
  const addTransactionWithCb = useTransactionAdderWithCallback()
  const { isTwitterBound, status } = useTwitterIsBound(account)
  const { width } = useWindowSize()
  const [selectedTaskId, setSelectedTaskId] = useState<null | number>(null)
  const tasks = useTasksByWallet({ account, userInfo })

  const slidesPerView = useMemo(() => {
    if (!width) return 3
    if (width > 850) {
      return 3
    }
    if (width > 600) {
      return 2
    }
    return 1
  }, [width])

  return (
    <Wrapper>
      <TotalInfoWrapper
        modules={[Autoplay, Pagination, Controller]}
        // effect="fade"
        // fadeEffect={{ crossFade: true }}
        speed={500}
        autoplay={{ delay: 5000, pauseOnMouseEnter: true, disableOnInteraction: false }}
        loop
        pagination={{ clickable: true }}
        id="swiper"
        slidesPerView={slidesPerView}
        spaceBetween={30}
        pt={!isTwitterBound || status === Status.PENDING ? '' : '30px'}
      >
        <BindTwitter />
        {tasks.map((task, i) => (
          <SwiperSlide key={`slide-${i}`} style={{ listStyle: 'none' }}>
            <TaskBlock
              competitionContract={competitionContract}
              userInfo={userInfo}
              task={task}
              action={() => {
                if (task.content) {
                  setSelectedTaskId(i)
                }
              }}
            />
          </SwiperSlide>
        ))}

        <Modal
          isOpen={selectedTaskId !== null}
          $scrollOverlay={true}
          onDismiss={() => setSelectedTaskId(null)}
          maxHeight={50}
          maxWidth={800}
        >
          <HiddenScroll>
            <CloseIconBg onClick={() => setSelectedTaskId(null)} data-testid="confirmation-close-icon" />
            {selectedTaskId !== null && (
              <ModalWrapper>
                <AutoColumn gap="sm">
                  <Row>
                    <Row justify="center" marginLeft="24px">
                      <ThemedText.SubHeader>{tasks[selectedTaskId]?.title}</ThemedText.SubHeader>
                    </Row>
                  </Row>
                  {tasks[selectedTaskId]?.content}
                </AutoColumn>
              </ModalWrapper>
            )}
          </HiddenScroll>
        </Modal>
      </TotalInfoWrapper>
    </Wrapper>
  )
}

export default Tasks
